import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import { IconArrowBack, IconBook } from "@tabler/icons-react"

interface Book {
  id: number
  title: string
}

interface User {
  id: number
  name: string
  avatar_url: string | null
}

interface Checkout {
  id: number
  book: Book
  user: User
  checked_out_at: string | null
  due_date: string | null
  returned_at: string | null
  status: string
  days_until_due: number | null
  days_overdue: number
}

interface StatusCounts {
  all: number
  active: number
  returned: number
  overdue: number
}

interface Props {
  [key: string]: unknown
  checkouts: Checkout[]
  pagy: PagyMeta
  status_counts: StatusCounts
  filters: { status?: string }
  is_admin: boolean
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default"
    case "returned":
      return "secondary"
    case "overdue":
      return "destructive"
    default:
      return "outline"
  }
}

export default function CheckoutsIndex() {
  const { checkouts, pagy, status_counts, filters, is_admin } = usePage<Props>().props

  function handleFilter(status?: string) {
    router.get("/checkouts", { status: status || undefined }, { preserveState: true })
  }

  function handleReturn(id: number) {
    if (confirm("Are you sure you want to return this book?")) {
      router.patch(`/checkouts/${id}/return`)
    }
  }

  return (
    <>
      <Head title="Checkouts - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/books">Library</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Checkouts</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Checkouts</h1>
        </div>

        <div className="flex items-center gap-2">
          {(
            [
              { label: "All", value: undefined, count: status_counts.all },
              { label: "Active", value: "active", count: status_counts.active },
              {
                label: "Returned",
                value: "returned",
                count: status_counts.returned,
              },
              {
                label: "Overdue",
                value: "overdue",
                count: status_counts.overdue,
              },
            ] as const
          ).map((f) => (
            <Button
              key={f.label}
              variant={filters.status === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleFilter(f.value)}
            >
              {f.label} ({f.count})
            </Button>
          ))}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Book</TableHead>
              {is_admin && <TableHead>User</TableHead>}
              <TableHead>Checked Out</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Returned</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {checkouts.map((checkout) => (
              <TableRow key={checkout.id}>
                <TableCell>
                  <a href={`/books/${checkout.book.id}`} className="font-medium text-primary hover:underline">
                    {checkout.book.title}
                  </a>
                </TableCell>
                {is_admin && <TableCell className="text-muted-foreground">{checkout.user.name}</TableCell>}
                <TableCell className="text-muted-foreground text-sm">{formatDate(checkout.checked_out_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground text-sm">{formatDate(checkout.due_date)}</span>
                    {checkout.status === "active" && checkout.days_until_due !== null && (
                      <Badge variant={checkout.days_until_due <= 2 ? "destructive" : "outline"} className="text-xs">
                        {checkout.days_until_due <= 0 ? "Due today" : `${checkout.days_until_due}d left`}
                      </Badge>
                    )}
                    {checkout.status === "overdue" && checkout.days_overdue > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {checkout.days_overdue}d overdue
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatDate(checkout.returned_at)}</TableCell>
                <TableCell>
                  <Badge variant={statusVariant(checkout.status)}>
                    {checkout.status.charAt(0).toUpperCase() + checkout.status.slice(1)}
                  </Badge>
                </TableCell>
                <TableCell>
                  {checkout.status === "active" || checkout.status === "overdue" ? (
                    <Button variant="outline" size="sm" onClick={() => handleReturn(checkout.id)}>
                      <IconArrowBack className="mr-1 h-4 w-4" />
                      Return
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" asChild>
                      <a href={`/books/${checkout.book.id}`}>
                        <IconBook className="mr-1 h-4 w-4" />
                        View Book
                      </a>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {checkouts.length === 0 && (
              <TableRow>
                <TableCell colSpan={is_admin ? 7 : 6} className="text-muted-foreground text-center py-8">
                  No checkouts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

CheckoutsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
